import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import GetAppIcon from "@mui/icons-material/GetApp";
import { IconButton, Stack } from "@mui/material";
import EmptyCardDataRooms from "assets/images/EmptyCardDataRooms.png";
import { ProgressOverlay, RoleComponentGuard } from "components";
import { FilesTable, FilesTableFilters, FilesTableMenu } from "components/data-rooms";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { defaultDataroomFilesFilters } from "constants/datarooms";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { Dayjs } from "dayjs";
import { useActionHook, useFormatDateHook, useQueryHook } from "hooks";
import {
  useConnectionSelect,
  useConnectionsSelect,
  useDataRoomsActions,
  useDateFormatSelect,
  useDealActions,
  usePickUserPermissions
} from "store";
import {
  DataItem,
  DataroomFilesFilters,
  DataroomFileTable,
  FilterOptions,
  IFile,
  IFileDataroom,
  UserPermissionType
} from "store/types";

import { RoleName } from "../../constants";

const defaultFilters: FilterOptions = {
  accountNameValues: [],
  docTypeValues: [],
  dealNameValues: [],
  altiusDealNameValues: []
};

interface PaginationState {
  page: number;
  perPage: number;
}

const defaultPagination: PaginationState = {
  page: 0,
  perPage: 50
};

interface Props {
  dataroomId?: number;
}

const allowedFileTypesToPreview = ["png", "jpeg", "jpg", "pdf"];

export const DataroomFiles: React.FC<Props> = ({ dataroomId }) => {
  const connection = useConnectionSelect();
  const connections = useConnectionsSelect();
  const cloudIntegrations = usePickUserPermissions("cloud_integrations");
  const isViewerPermission = cloudIntegrations && cloudIntegrations === UserPermissionType.VIEWER;
  const noAccessPermission = cloudIntegrations && cloudIntegrations === UserPermissionType.NO_ACCESS;
  const dateFormatHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDate = (date?: string | number | Date | Dayjs | null | undefined) => {
    return dateFormatHook({ date, format: dateFormat + " HH:mm" });
  };
  const [selectedItems, setSelectedItems] = useState<DataItem[]>([]);
  const [filtersPaging, setFiltersPaging] = useState<{ pagination: PaginationState; filters: DataroomFilesFilters }>({
    pagination: defaultPagination,
    filters: { ...defaultDataroomFilesFilters, dataroomsIds: dataroomId ? [dataroomId] : [] }
  });

  const [sortBy, setSortBy] = useState<string>("created_at");
  const [isDesc, setIsDesc] = useState<boolean>(true);
  const [filePreview, setFilePreview] = useState<IFile | null>(null);
  const [updatedFiles, setUpdatedFiles] = useState<Map<number, IFile>>(new Map());
  const [showAllColumns, setShowAllColumns] = useState<boolean>(false);

  const { getAllFilesByDRAction, getFileAction, downloadFileFromDRAction } = useDealActions();
  const { downloadDataroomsFiles, getFilesFiltersAction, getConnectionsAction } = useDataRoomsActions();

  // Load connections for the connection status icon
  useQueryHook(getConnectionsAction);

  const {
    action: getAllFiles,
    data: allFilesResponse,
    loading: getAllFilesLoading
  } = useActionHook(getAllFilesByDRAction);
  const { action: sendFiles, loading: sendFilesLoading } = useActionHook((op: string) =>
    downloadDataroomsFiles(selectedItems, op)
  );

  const {
    action: getFilters,
    loading: filtersLoading,
    data: filters = defaultFilters
  } = useActionHook(getFilesFiltersAction);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setShowAllColumns(urlParams.get("showAllColumns") === "1");
  }, []);

  const columns: DataroomFileTable["columns"] = useMemo(() => {
    const baseColumns = [
      { key: "name", title: "File Name" },
      { key: "accountName", title: "Account Name" },
      { key: "docType", title: "Document Type" },
      { key: "altiusClassification", title: "Altius classification" },
      { key: "effective_at", title: "Effective" },
      { key: "created_at", title: "Indexed" },
      { key: "downloaded_at", title: "Sync" },
      { key: "dataroom", title: "Dataroom" }
    ];

    if (showAllColumns) {
      baseColumns.unshift({ key: "id", title: "ID" });

      const syncColumnIndex = baseColumns.findIndex((col) => col.key === "downloaded_at");
      baseColumns.splice(
        syncColumnIndex,
        0,
        { key: "indexed_at", title: "Index" },
        { key: "uploaded_at", title: "Upload" }
      );
    }

    return baseColumns;
  }, [showAllColumns]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, item: DataItem) => {
    if (event.target.checked) {
      if (
        (!item.downloaded_at || !item.downloaded_at.length) &&
        (!connection?.is_login_valid ||
          new Date().getTime() - Date.parse(connection?.last_connected || "1970-01-01") > 60 * 60 * 1000)
      ) {
        toast.error("Please reconnect to download this file.");
        return;
      }
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
    }
  };

  const transformDataToTable = (files?: IFileDataroom[]): DataroomFileTable => {
    const result: DataroomFileTable = { rows: [], columns };

    if (!files) return result;
    result.rows = files.map((file) => {
      // Check if this file was recently updated
      const fileId = typeof file.id === "number" ? file.id : parseInt(file.id, 10);
      const updatedFile = updatedFiles.get(fileId);
      // If we have an updated file, merge its properties with the original file
      const fileToUse: IFileDataroom = updatedFile ? ({ ...file, ...updatedFile } as IFileDataroom) : file;

      const row = {
        name: fileToUse.name,
        accountName: fileToUse.tags.accountName,
        docType: fileToUse.tags.docType,
        altiusClassification: fileToUse.tags.altius_type ?? fileToUse.altius_type ?? "",
        created_at: formatDate(fileToUse.created_at),
        downloaded_at: formatDate(fileToUse.downloaded_at),
        uploaded_at: fileToUse.uploaded_at,
        effective_at: fileToUse.effective_at,
        indexed_at: fileToUse.indexed_at,
        last_connected: fileToUse.last_connected,
        id: fileToUse.id,

        dataroom: fileToUse.tags.dataroom || fileToUse.dataroom,
        dealId: fileToUse.tags.dealId,
        dealName: fileToUse.tags.dealName,
        fetcherId: fileToUse.tags.fetcherId,
        fundId: fileToUse.tags.fundId,
        fundName: fileToUse.tags.fundName
      };

      return row;
    });

    return result;
  };

  const handleAddAllFiles = useCallback(
    async (value: boolean) => {
      if (!value) {
        return setSelectedItems([]);
      }

      const response = await getAllFiles({
        filters: filtersPaging.filters,
        pageNumber: filtersPaging.pagination.page,
        pageSize: filtersPaging.pagination.perPage,
        sortBy: sortBy,
        isDescending: isDesc,
        ...(dataroomId && { dataroomsIds: [dataroomId] })
      });

      const { rows } = transformDataToTable(response.files);

      const values: DataItem[] = rows.map((row) => {
        return {
          id: row?.id as string,
          name: row.name,
          dealId: row.fundId,
          fetcherId: row.fetcherId,
          downloaded_at: row.downloaded_at || null,
          last_connected: row.last_connected || null
        };
      });
      if (
        !connection?.is_login_valid ||
        new Date().getTime() - Date.parse(connection?.last_connected || "1970-01-01") > 60 * 60 * 1000
      ) {
        toast.error("Please reconnect to download All unsynced files.");
        setSelectedItems(values.filter((item) => item.downloaded_at && item.downloaded_at.length));
      } else {
        setSelectedItems(values);
      }
      toast.success(Math.min(response?.total_files ?? 0, 1000) + " files selected.");
    },
    [transformDataToTable, filtersPaging, sortBy, isDesc, dataroomId]
  );

  const setFilters = useMemo<(callback: (filters: DataroomFilesFilters) => DataroomFilesFilters) => void>(() => {
    return (cb) => {
      setFiltersPaging((prevState) => ({ pagination: prevState.pagination, filters: cb(prevState.filters) }));
    };
  }, [setFiltersPaging]);

  const updatePagination = useCallback(
    (data: Partial<PaginationState>) => {
      setFiltersPaging((prevState) => ({ ...prevState, pagination: { ...prevState.pagination, ...data } }));
    },
    [setFiltersPaging]
  );

  const handleFileClick = useCallback(
    async (dataroomFile: DataroomFileTable["rows"][0]): Promise<void> => {
      try {
        let file: IFile | null = null;
        const dataroomFileId = dataroomFile.id;
        const dataroomFileDealId = dataroomFile.dealId;
        if (typeof dataroomFileId !== "number" || !dataroomFileDealId) return;
        toast.info("Downloading file in progress", { position: "top-center", autoClose: 30000 });

        // Check if file is actually downloaded (not just "-" or empty string)
        const isFileDownloaded =
          dataroomFile.downloaded_at && dataroomFile.downloaded_at !== "-" && dataroomFile.downloaded_at !== "";

        // Get file and show it (the show endpoint will download if needed)
        if (isFileDownloaded) {
          file = await getFileAction({ fileId: dataroomFileId, dealId: dataroomFileDealId });
        }
        // Download file, update it in the storage, table, and show it
        if (!isFileDownloaded) {
          // Try to download the file - the API will handle authentication if needed
          try {
            file = await downloadFileFromDRAction({ fileId: dataroomFileId, dealId: dataroomFileDealId });
            // Update the local state with the downloaded file to reflect in the table
            if (file) {
              // Map IFile to IFileDataroom format
              const fileDataroom: Partial<IFileDataroom> = {
                downloaded_at: file.dataroom_file?.file_downloaded || null
              };

              setUpdatedFiles((prev) => {
                const newMap = new Map(prev);
                newMap.set(dataroomFileId, fileDataroom as any);
                return newMap;
              });
            }
          } catch (error: any) {
            toast.dismiss();
            // If it's an authentication error, show a helpful message
            if (error?.response?.status === 422 || error?.message?.includes("Authentication")) {
              toast.error("file can't be downloaded, please reconnect and try again.", { position: "top-center" });
            } else {
              toast.error("Failed to download file.", { position: "top-center" });
            }
            return;
          }
        }

        toast.dismiss();

        if (!file) return;
        const { type: fileType } = file;
        const isFileAllowed = allowedFileTypesToPreview.includes(fileType);
        if (!isFileAllowed) return;
        setFilePreview(file);
      } catch (err: any) {
        toast.dismiss();
        if (err?.errors == "File not found") {
          toast.error(err?.errors, { position: "top-center" });
        } else {
          toast.error("file can't be downloaded, please reconnect and try again.", { position: "top-center" });
        }
      }
    },
    [filtersPaging, sortBy, isDesc, connection]
  );

  useEffect(() => {
    // if (selectedItems.length) {
    //   setSelectedItems([]);
    // }
    getAllFiles({
      filters: filtersPaging.filters,
      pageNumber: filtersPaging.pagination.page,
      pageSize: filtersPaging.pagination.perPage,
      sortBy: sortBy,
      isDescending: isDesc,
      ...(dataroomId && { dataroomsIds: [dataroomId] })
    });
  }, [filtersPaging, sortBy, isDesc]);

  useEffect(() => {
    getFilters({ ...filtersPaging.filters, dataroomsIds: dataroomId ? [dataroomId] : [] });
  }, []);

  const data = useMemo(
    () => transformDataToTable(allFilesResponse?.files || []),
    [allFilesResponse?.files, updatedFiles, formatDate]
  );

  // Check if at least one selected file has an active connection
  const hasActiveConnection = useMemo(() => {
    if (!selectedItems.length || !connections.length) return false;

    // Get all files data
    const allFiles = allFilesResponse?.files || [];

    // Check if any selected file's dataroom has an active connection
    return selectedItems.some((selectedItem) => {
      // Find the file data for this selected item
      const file = allFiles.find((f) => {
        const fileId = typeof f.id === "number" ? f.id : parseInt(f.id, 10);
        const selectedId = typeof selectedItem.id === "number" ? selectedItem.id : parseInt(selectedItem.id || "", 10);
        return fileId === selectedId;
      });

      if (!file) return false;

      const dataroom = file.tags?.dataroom || file.dataroom;
      if (!dataroom) return false;

      // Match dataroom to connection
      const dataroomStr = String(dataroom).trim();
      const connection = connections.find((conn) => {
        const connTitle = String(conn.type_title || "")
          .trim()
          .toLowerCase();
        const dataroomLower = dataroomStr.toLowerCase();
        if (connTitle === dataroomLower) return true;
        if (dataroomLower.startsWith(connTitle + " ")) return true;
        if (dataroomLower.includes(connTitle)) return true;
        return false;
      });

      return connection?.is_login_valid === true;
    });
  }, [selectedItems, connections, allFilesResponse?.files]);

  // Check if at least one selected file is synced
  const hasAtLeastOneSynced = useMemo(() => {
    return selectedItems.some((item) => item.downloaded_at && item.downloaded_at !== "-" && item.downloaded_at !== "");
  }, [selectedItems]);

  const hasActiveFilters = useMemo(() => {
    const filters = filtersPaging.filters;
    return (
      filters.deals.length ||
      filters.accounts.length ||
      filters.docTypes.length ||
      filters.altiusDeals.length ||
      filters.createdAtDate !== null ||
      filters.syncAtDate !== null ||
      filters.effectiveAtDate !== null ||
      filters.uploadedAtDate !== null ||
      !filters.showLatest ||
      !filters.synced ||
      !filters.notSynced
    );
  }, [filtersPaging.filters]);

  const isEmptyState =
    !getAllFilesLoading && !filtersLoading && allFilesResponse && !allFilesResponse.files?.length && !hasActiveFilters;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
    >
      <ProgressOverlay
        inProgress={sendFilesLoading || getAllFilesLoading || filtersLoading}
        containerSX={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
          background: "white",
          padding: "24px",
          borderRadius: "8px",
          height: "100%"
        }}
        spinnerProps={{ size: 120 }}
      >
        {isEmptyState ? (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.dataRoomsAllFiles.title}
            description={{
              text: EMPTY_CARD_CONTENT.dataRoomsAllFiles.description,
              externalText: EMPTY_CARD_CONTENT.dataRoomsAllFiles.externalDescription
            }}
            image={{
              src: EmptyCardDataRooms,
              sx: { width: 315, height: 140 }
            }}
          />
        ) : (
          <>
            <Stack flexDirection="row" justifyContent="space-between" sx={{ alignItems: "flex-start", mb: "14px" }}>
              <FilesTableFilters
                filters={filtersPaging.filters}
                setFilters={setFilters}
                allFilterOptions={{
                  accounts: filters.accountNameValues,
                  docTypes: filters.docTypeValues,
                  deals: filters.dealNameValues,
                  altiusDeals: filters.altiusDealNameValues
                }}
                refetchFiltersOptions={(selectedFilters) => getFilters(selectedFilters)}
              />
              <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
                <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
                  <FilesTableMenu
                    enableSync={selectedItems.some((item) => !item.downloaded_at || item.downloaded_at === "-")}
                    isLoading={sendFilesLoading}
                    setFiles={sendFiles}
                    isConnectionValid={
                      connections.length > 0 &&
                      connections.every(
                        (conn) =>
                          !!conn.is_login_valid &&
                          (!conn.last_connected ||
                            new Date().getTime() - Date.parse(conn.last_connected) <= 60 * 60 * 1000)
                      )
                    }
                    allFilesSynced={
                      selectedItems.length > 0 &&
                      selectedItems.every(
                        (item) => item.downloaded_at && item.downloaded_at !== "-" && item.downloaded_at !== ""
                      )
                    }
                    hasSelectedFiles={!!selectedItems.length}
                    hasActiveConnection={hasActiveConnection}
                    hasAtLeastOneSynced={hasAtLeastOneSynced}
                  />
                  <div style={line}></div>
                  <IconButton
                    disabled={selectedItems.length == 0 || sendFilesLoading}
                    onClick={() => sendFiles("download")}
                  >
                    <GetAppIcon sx={{ fontSize: "27px" }} color={selectedItems.length == 0 ? "disabled" : "inherit"} />
                  </IconButton>
                </Stack>
              </RoleComponentGuard>
            </Stack>
            <Stack sx={{ flexGrow: 1, overflow: "hidden" }}>
              <FilesTable
                data={data}
                sortBy={sortBy}
                setSortBy={setSortBy}
                isDesc={isDesc}
                setIsDesc={setIsDesc}
                selectAll={handleAddAllFiles}
                onCheckboxChange={handleCheckboxChange}
                selectedItems={selectedItems}
                {...(allFilesResponse && { total_files: allFilesResponse.total_files })}
                paginationData={filtersPaging.pagination}
                setPaginationData={updatePagination}
                filePreview={filePreview}
                handleCloseFilePreview={() => setFilePreview(null)}
                onFileClick={handleFileClick}
              />
            </Stack>
          </>
        )}
      </ProgressOverlay>
    </RoleComponentGuard>
  );
};

const line = {
  width: 1,
  height: 18,
  flexGrow: 0,
  backgroundColor: "#dae0e2"
};
